/* eslint-disable no-bitwise */
import {
  Directive,
  HostBinding,
  HostListener,
  Inject,
  Input,
  OnChanges,
  Self,
  SimpleChanges,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControlDirective,
  FormControlName,
  NgControl,
} from '@angular/forms';
import { TypedFormControl } from '@assetaccountant/core/utils/typed-forms';

/**
 * Use this directive for bit-flag inputs
 * This allows multiple checkboxes to update different parts of the same value,
 * so a value of 7 could be made from 3 checkboxes representing 1, 2 & 4.
 * See Create Journal for an example
 */
@Directive({
  selector:
    'input[type=checkbox][formControlName][appFlagInput], input[type=checkbox][formControl][appFlagInput]',
  standalone: false,
})
export class FlagInputDirective implements ControlValueAccessor, OnChanges {
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('appFlagInput') flag: number;

  @Input() flagDisabled = false;

  get formControl() {
    return this.ngControl.control as TypedFormControl<number>;
  }

  state: 'CHECKED' | 'UNCHECKED' | 'INDETERMINATE' = 'UNCHECKED';

  @HostBinding() get checked() {
    return !this.flagDisabled && this.state === 'CHECKED';
  }
  @HostBinding() get indeterminate() {
    return this.state === 'INDETERMINATE';
  }

  private controlDisabled = false;
  @HostBinding() get disabled() {
    return this.controlDisabled || this.flagDisabled;
  }

  private _onTouched?: () => void;

  constructor(
    @Self() @Inject(NgControl) private readonly ngControl: FormControlName | FormControlDirective,
  ) {
    this.ngControl.valueAccessor = this;
  }

  writeValue(obj: number) {
    /*
     * Example `state` where flag = 3
     * - value === 3 -> 'CHECKED'
     * - value === 2 -> 'INDETERMINATE'
     * - value === 0 -> 'UNCHECKED'
     */

    const value = +obj & this.flag;

    if (this.flagDisabled) {
      if (value) {
        if (value === this.flag) {
          this.state = 'CHECKED';
        }
        setTimeout(() => {
          if (this.formControl?.value) {
            this.formControl.setValue(+this.formControl.value & ~this.flag);
          }
        });
      }
    } else if (value === this.flag) {
      this.state = 'CHECKED';
    } else if (value) {
      this.state = 'INDETERMINATE';
    } else {
      this.state = 'UNCHECKED';
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if ('flagDisabled' in changes) {
      if (this.flagDisabled || this.state === 'UNCHECKED') {
        setTimeout(() => {
          if (this.formControl?.value) {
            this.formControl.setValue(+this.formControl.value & ~this.flag);
          }
        });
      } else if (this.state === 'CHECKED') {
        setTimeout(() => {
          if (this.formControl) {
            this.formControl.setValue(+this.formControl.value | this.flag);
          }
        });
      }
    }
  }

  @HostListener('change', ['$event.target.checked'])
  onChange(checked: boolean) {
    const value = this.formControl.value;
    const newValue = checked ? value | this.flag : value & ~this.flag;

    this.state = checked ? 'CHECKED' : 'UNCHECKED';

    this.formControl.setValue(newValue);
    this.formControl.markAsDirty();
  }

  @HostListener('blur')
  onTouched() {
    this._onTouched?.();
  }

  registerOnChange(_fn: (value: number) => void) {
    // Ignore this as we set it directly on the form control
  }
  registerOnTouched(fn: () => void) {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this.controlDisabled = isDisabled;
  }
}
